<template>
  <div class="ai-chat-surface">
    <div class="_header">
      <slot name="header-start" />
      <ai-chat-history-dropdown
        class="_title"
        :chat-id="chatId"
        @select="$emit('select-chat', $event)"
      />
      <div class="_actions">
        <m-tooltip placement="bottom">
          <template #title>
            {{ $t('aiChat.newChat') }}
          </template>
          <m-btn
            icon="plus"
            light
            hide-border
            fab
            small
            @click="$emit('new-chat')"
          />
        </m-tooltip>
        <m-tooltip
          v-if="activeChat"
          placement="bottom"
        >
          <template #title>
            {{ pinned ? $t('aiChat.unpin') : $t('aiChat.pin') }}
          </template>
          <m-btn
            class="_pin"
            :icon="pinned ? 'pushpin-filled' : 'pushpin'"
            light
            hide-border
            fab
            small
            @click="onTogglePin"
          />
        </m-tooltip>
        <ai-chat-chat-menu
          v-if="chatId !== null"
          :chat-id="chatId"
        />
        <slot name="header-actions" />
      </div>
    </div>
    <div :class="['_body', welcome ? '-welcome' : '']">
      <ai-chat-message-list
        class="_messages"
        :chat-id="chatId"
        :boxed="boxed"
        :messages="messages"
        :outgoing="outgoing"
        :pending="pending"
        :streamed-answer="streamedAnswer"
        :connection="connection"
        :failure="failure"
        :loading="getChatLoading"
        :retryable="retryable"
        @retry="onRetry"
        @use-skill="applySkill"
      />
      <ai-chat-composer
        ref="composer"
        :key="chatId"
        :chat-id="chatId"
        :boxed="boxed"
        :pending="pending"
        :send="sendMessage"
        :stoppable="stoppable"
        :stop="onStop"
      />
      <ai-chat-skill-suggestions
        v-if="welcome"
        class="_suggestions"
        @use="applySkill"
      />
    </div>
  </div>
</template>

<script setup>
import AiChatChatMenu from '@/components/ai-chat/AiChatChatMenu.vue';
import AiChatComposer from '@/components/ai-chat/AiChatComposer.vue';
import AiChatHistoryDropdown from '@/components/ai-chat/AiChatHistoryDropdown.vue';
import AiChatMessageList from '@/components/ai-chat/AiChatMessageList.vue';
import AiChatSkillSuggestions from '@/components/ai-chat/AiChatSkillSuggestions.vue';
import useAiChatConversation from '@/composables/ai-chat/ai-chat-conversation';
import useAiChatPin from '@/composables/ai-chat/ai-chat-pin';
import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import useSnackBar from '@/composables/snackbar';
import { clearSession } from '@/composables/ai-chat/ai-chat-session';
import { computed, ref, toRef, watch } from 'vue';
import { isEmptyChat } from '@/lib/ai-chat-list';
import { logCatch } from '@/lib/logger/logger';

const props = defineProps({
  chatId: {
    type: Number,
    default: null,
  },
  boxed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['new-chat', 'select-chat', 'chat-created', 'chat-deleted']);

const { queryChat, selectChat, getChatLoading } = useAiChatRepo();
const onChatCreated = (uid) => emit('chat-created', uid);
const {
  messages,
  outgoing,
  pending,
  streamedAnswer,
  connection,
  failure,
  sendMessage,
  retryable,
  retry,
  stoppable,
  stop,
} = useAiChatConversation(toRef(props, 'chatId'), { onChatCreated });

const snackbar = useSnackBar();
const onRetry = () => retry().catch(logCatch());
const onStop = () => stop().catch(logCatch(() => snackbar.error()));

const composer = ref(null);
const applySkill = (skill) => composer.value.applySkill(skill);

const welcome = computed(() => props.boxed && isEmptyChat({
  messages: messages.value,
  outgoing: outgoing.value,
  pending: pending.value,
  failure: failure.value,
  loading: getChatLoading.value,
}));

const activeChat = computed(() => (props.chatId === null ? null : selectChat(props.chatId)));

const { pinned, toggle: onTogglePin } = useAiChatPin(activeChat);

watch(() => props.chatId, (chatId) => {
  if (chatId !== null) {
    queryChat(chatId);
  }
}, { immediate: true });

// deleting the open chat drops it from the store before the menu that triggered the
// delete is unmounted, so the surface watches the chat itself rather than the event
watch(activeChat, (chat, previous) => {
  if (chat === undefined && previous?.uid === props.chatId) {
    clearSession(props.chatId);
    emit('chat-deleted');
  }
});
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-surface {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;

    ._header {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: .4rem;
      padding: .8rem 1.2rem;

      &::after {
        content: '';
        position: absolute;
        top: 100%;
        right: 1.2rem;
        left: 0;
        height: $ai-chat-fade-height;
        background: linear-gradient(to bottom, white, rgba(255, 255, 255, 0));
        pointer-events: none;
      }
    }

    ._title {
      min-width: 0;
    }

    ._actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: .2rem;
      margin-left: auto;
    }

    ._body {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;

      &.-welcome {
        justify-content: center;
        padding-bottom: 10vh;

        ._messages {
          flex: 0 0 auto;
        }
      }
    }

    ._suggestions {
      width: 100%;
      max-width: calc(#{$ai-chat-full-screen-width} + 2.4rem);
      margin: 0 auto;
      padding: 0 1.2rem;
    }
  }
</style>
